const KEY_LEN = 32;
const IV_LEN = 12;
const TAG_LEN = 16;

/**
 * Field encryption for deal content.
 *
 * WebCrypto rather than node:crypto, so one implementation runs unchanged in
 * Node and in the browser. That matters beyond convenience: the deal key is
 * generated and split in the counterparty's browser (chain design §7.2), so the
 * platform never holds it — and a second, browser-only copy of an AEAD is
 * exactly where a silent divergence would live.
 */

/** 32 random bytes. One per deal; stored, never derived (§7.2). */
export function newDek(): Buffer {
  const bytes = new Uint8Array(KEY_LEN);
  crypto.getRandomValues(bytes);
  return Buffer.from(bytes);
}

/**
 * Binds a ciphertext to its position. A blob lifted from one deal or one field
 * fails authentication anywhere else (§7.2 step 2).
 */
export function aad(dealId: string, field: string, version: number): Buffer {
  return Buffer.from(`${dealId}|${field}|${version}`, "utf8");
}

function assertKey(dek: Buffer): void {
  if (dek.length !== KEY_LEN) throw new Error(`dek must be ${KEY_LEN} bytes, got ${dek.length}`);
}

function importKey(dek: Buffer, usage: "encrypt" | "decrypt"): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", dek, { name: "AES-GCM" }, false, [usage]);
}

/**
 * Returns iv(12) || tag(16) || ciphertext.
 *
 * WebCrypto returns ciphertext with the tag APPENDED; the wire format puts the
 * tag first. The re-ordering below is a compatibility contract with every blob
 * already written to the chain — see the golden-vector test.
 */
export async function encryptField(
  dek: Buffer, plaintext: Buffer, associated: Buffer,
): Promise<Buffer> {
  assertKey(dek);
  const iv = new Uint8Array(IV_LEN);
  crypto.getRandomValues(iv);

  const key = await importKey(dek, "encrypt");
  const sealed = new Uint8Array(await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: associated, tagLength: TAG_LEN * 8 },
    key,
    plaintext,
  ));

  const ct = sealed.subarray(0, sealed.length - TAG_LEN);
  const tag = sealed.subarray(sealed.length - TAG_LEN);
  return Buffer.concat([Buffer.from(iv), Buffer.from(tag), Buffer.from(ct)]);
}

/** Rejects if the key, the AAD, or any byte of the blob is wrong. */
export async function decryptField(
  dek: Buffer, blob: Buffer, associated: Buffer,
): Promise<Buffer> {
  assertKey(dek);
  if (blob.length < IV_LEN + TAG_LEN) throw new Error("blob too short");

  const iv = blob.subarray(0, IV_LEN);
  const tag = blob.subarray(IV_LEN, IV_LEN + TAG_LEN);
  const ct = blob.subarray(IV_LEN + TAG_LEN);

  const key = await importKey(dek, "decrypt");
  // WebCrypto expects ciphertext || tag, the reverse of the wire order.
  const sealed = Buffer.concat([ct, tag]);

  const out = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv, additionalData: associated, tagLength: TAG_LEN * 8 },
    key,
    sealed,
  );
  return Buffer.from(new Uint8Array(out));
}
